// 捲動進場：標了 data-reveal 的元素捲進畫面才加上 is-in，
// 這樣才會像動畫效果.mp4 那樣一區一區進場，而不是一載入就全部播完。
// 離開畫面會把 is-in 收回來，捲回去時整段動畫會重播一次（整屏分頁常常來回捲，
// 只播一次的話回頭看就變成一張靜止的圖）。
// 用法：頁面 setup 裡呼叫 useReveal()，元素加 data-reveal（要錯開就再加 style="--i: 1"）。
export function useReveal(rootRef) {
  let io = null

  // 進場動畫本身會把元素移到畫面外（雲朵是從左側 -22vw 飄進來的，而 .scroller 有
  // overflow-x:hidden，起點整個被裁掉），observer 於是判定它「離開畫面」而收回 is-in，
  // 動畫重播又立刻移出去，死循環卡在第一幀 opacity:0——看起來就是那朵雲整個不見。
  // 所以還在播進場動畫的元素一律不收：雲朵飄浮那種無限循環的動畫時長是 Infinity，
  // 不會被誤判成進場中，捲離畫面後照樣能收回來重播。
  const isEntering = (el) =>
    typeof el.getAnimations === 'function' &&
    el
      .getAnimations()
      .some(
        (a) =>
          a.playState === 'running' && Number.isFinite(a.effect?.getComputedTiming().activeDuration)
      )

  onMounted(() => {
    const root = rootRef?.value || document
    const items = Array.from(root.querySelectorAll('[data-reveal]'))
    if (!items.length) return

    // 不支援 IntersectionObserver 或使用者要求減少動態時，直接顯示，不做動畫
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver === 'undefined') {
      items.forEach((el) => el.classList.add('is-in'))
      return
    }

    io = new IntersectionObserver(
      (entries) => {
        // 捲回來還要再播一次，所以不 unobserve，離開畫面就把狀態收回去
        const out = []
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-in')
          else if (!isEntering(entry.target)) out.push(entry.target)
        })
        if (!out.length) return

        // 收回時若讓它跑一次反向動畫，馬上又捲回來就會接在淡出到一半的狀態上，
        // 進場變得半吊子。先關掉 transition 直接歸零，強制回流沖進去之後再交還給 CSS，
        // 回流整批只做一次，免得離開畫面的元素一多就卡住捲動
        out.forEach((el) => {
          el.style.transition = 'none'
          el.classList.remove('is-in')
        })
        void document.body.offsetWidth
        out.forEach((el) => {
          el.style.transition = ''
        })
      },
      // 用固定 px 而不是百分比：整屏分頁時區塊底部的元素會落在百分比邊界內，永遠觸發不到
      { rootMargin: '0px 0px -40px 0px', threshold: 0.05 }
    )
    items.forEach((el) => io.observe(el))
  })

  onBeforeUnmount(() => {
    io?.disconnect()
    io = null
  })
}
